import axios from "axios";
import { errorHandler } from "globalResources/util";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Row } from "globalComponents";
import styled from "styled-components";
import moment from "moment-timezone";
import { useState } from "react";
import { formatDollars } from "globalResources/helpers";
import { addNotification } from "store/slices/uiSlice";
import { notifTags } from "globalComponents/Notification";

const Main = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 20px;
  padding: 10px;
  width: 550px;
  height: 100%;
  margin-left: "auto";
  margin-top: 34px;
  gap: 5px;
`;
const TotalRow = styled.div`
  display: flex;
  justify-items: space-between;
  width: 100%;
`;

const TotalLabel = styled.div`
  font-weight: 600;
  font-size: 16px;
`;
const Total = styled.div`
  font-weight: 500;
  font-size: 14px;
  margin-left: auto;
`;

const TotalsSection = ({
  getAmount,
  setState,
  props,
  state,
  report,
  currUser,
  refresh,
}) => {
  const [downloading, setDownloading] = useState(false);
  const currentUser = useSelector((state) => state.current.user);
  const treatClient = currentUser.accountType === "client";
  const dispatch = useDispatch();

  const totals =
    currUser === 1
      ? []
      : [
          {
            label: "Total Hours: ",
            value: `${getAmount(currUser).hours.toFixed(2)} hours`,
          },
          {
            label: "Overtime Hours: ",
            value: `${getAmount(currUser).overtimeHours.toFixed(2)} hours`,
          },
          {
            label: "Overtime Paid: ",
            value: `$${formatDollars(getAmount(currUser).overtimePaid)}`,
            nonClient: true,
          },
          {
            label: "Overtime Billed: ",
            value: `$${formatDollars(getAmount(currUser).overtimeBilled)}`,
          },
          {
            label: "Total Employee Pay: ",
            value: `$${formatDollars(
              getAmount(currUser).paid + getAmount(currUser).overtimePaid,
            )}`,
            nonClient: true,
          },
        ];
  totals.push({
    label: "Total Billed: ",
    value: `$${formatDollars(
      getAmount(currUser).billed + getAmount(currUser).overtimeBilled,
    )}`,
  });
  if (currUser < 2) {
    totals.push({
      label: "Adjustments",
      value: `$${formatDollars(getAmount(0).adjustments || 0)}`,
    });
    totals.push({
      label: "Amount Owed: ",
      value: `$${formatDollars(
        getAmount(currUser).billed +
          getAmount(currUser).overtimeBilled +
          getAmount(0).adjustments,
      )}`,
    });
  }

  totals.push({
    label: "Profit: ",
    value: `$${formatDollars(getAmount(currUser).profit)}`,
    nonClient: true,
  });

  function sendReview(id) {
    axios
      .put(
        `${process.env.REACT_APP_API_URL}/${
          treatClient ? "client/" : ""
        }report/send`,
        {
          id,
        },
      )
      .then(async (res) => {
        setState({
          ...state,
          message: "Report has been sent for review",
        });
        refresh();
      })
      .catch(async (err) => {
        const { message } = await errorHandler(err);
        setState({ ...state, loading: false, message });
      });
  }

  function billClient(id) {
    axios
      .post(`${process.env.REACT_APP_API_URL}/report/bill`, {
        id: id,
      })
      .then(async (res) => {
        setState({ ...state, message: "Report has been billed to the client" });
        refresh();
      })
      .catch(async (err) => {
        const { message } = await errorHandler(err);
        setState({ ...state, loading: false, message });
      });
  }

  const downloadReport = (view) => {
    setDownloading(true);
    setState({ ...state });
    axios
      .get(`${process.env.REACT_APP_API_URL}/report/invoice`, {
        responseType: "blob",
        headers: {
          "Content-Type": "application/pdf",
        },
        params: { id: report._id, view },
      })
      .then(({ data }) => {
        const downloadUrl = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute(
          "download",
          `${report.client.name}-${
            moment.tz(report.start, report.client.timezone).format("L") +
            " - " +
            moment.tz(report.end, report.client.timezone).format("L")
          }-invoice#${report.number}.pdf`,
        ); //any other extension
        document.body.appendChild(link);
        link.click();
        link.remove();
        setState({ ...state });
      })
      .catch((error) => {
        console.error("File could not be downloaded:", error);
        setState({
          ...state,
          message: "Error: could not download report",
        });
      })
      .finally(() => setDownloading(false));
  };

  const renotifyClient = async () => {
    setDownloading(true);
    setState({ ...state });
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/report/notify`, {
        id: report._id,
      });
      dispatch(
        addNotification({
          type: notifTags.success,
          message: "Invoice Resent to Client",
        }),
      );
    } catch (error) {
      console.error("Could not renotify client:", error);
    } finally {
      setDownloading(false);
      setState({ ...state });
    }
  };

  return (
    <Main>
      {report.status === "errored" || report.status === "generated" ? (
        <div style={{ marginLeft: "auto" }}>
          <Button onClick={() => sendReview(report._id)}>
            Send for Review
          </Button>
        </div>
      ) : report.status === "in-review" ? (
        <div>
          {!treatClient && (
            <Button onClick={() => billClient(report._id)}>
              Approve Report
            </Button>
          )}
        </div>
      ) : report.status === "client-review" ? (
        <div>
          {treatClient && (
            <Button onClick={() => billClient(report._id)}>
              Approve Report
            </Button>
          )}
        </div>
      ) : (
        <Row style={{ marginLeft: "auto", gap: "10px" }}>
          <Button
            loading={downloading}
            onClick={() => {
              downloadReport("compact");
            }}
          >
            Download Compact Invoice
          </Button>
          <Button disabled={downloading} onClick={() => renotifyClient()}>
            Resend Invoice
          </Button>
          <Button
            loading={downloading}
            onClick={() => {
              downloadReport();
            }}
          >
            Download Invoice
          </Button>
        </Row>
      )}
      {totals.map((total) =>
        treatClient && total.nonClient ? null : (
          <TotalRow>
            <TotalLabel>{total.label}</TotalLabel>
            <Total>{total.value}</Total>
          </TotalRow>
        ),
      )}
    </Main>
  );
};
export default TotalsSection;
